/**
 * magneticMonopolePipeline — an isolated magnetic charge.
 *
 * A magnetic monopole is a hypothetical particle with a single
 * magnetic pole (only north or only south). Maxwell's equations
 * predict that a magnetic monopole would have a field that radiates
 * outward (or inward) radially, like an electric charge's field.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MagneticMonopolePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.04, 0.04, 0.10), vec3(0.01, 0.01, 0.03), smoothstep(0.0, 0.7, r));

  vec3 col = bg;

  // The monopole: a bright source at the center.
  vec2 mpPos = vec2(0.0, 0.0);
  float mpD = length(p - mpPos);
  // Inner glow.
  col += vec3(1.0, 0.95, 0.7) * exp(-mpD * 50.0) * 1.5;
  col += vec3(0.95, 0.5, 0.3) * exp(-mpD * 12.0) * 0.6;
  col += vec3(0.7, 0.3, 0.4) * exp(-mpD * 4.0) * 0.3;

  // Concentric field-strength rings (like iron filings around a magnet).
  // Each ring is a contour of constant field strength. For a monopole
  // in 2D, B ~ 1/r, so the rings have uniform spacing.
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    float ringR = 0.08 + fi * 0.05;
    // Field strength at this radius: 1/r.
    float strength = 1.0 / (ringR + 0.05);
    // The ring is at radius ringR, drawn with brightness proportional
    // to its strength.
    float ringD = abs(r - ringR);
    float ringMask = exp(-ringD * 80.0);
    // Color: warm at high strength, cool at low.
    vec3 ringCol = mix(vec3(0.95, 0.4, 0.3), vec3(0.6, 0.5, 0.95), fi / 7.0);
    col += ringCol * ringMask * strength * 1.2;
  }

  // The Dirac string: a dashed line from the monopole to the edge.
  // Direction: depends on time and audio.
  vec2 stringDir = vec2(cos(uTime * 0.3 + bass), sin(uTime * 0.3 + bass));
  // The string is the ray from the monopole in this direction.
  // Sample the string at multiple points and find distance.
  float minD = 1e9;
  for (int k = 0; k < 30; k++) {
    float fk = float(k) / 29.0;
    vec2 pt = mpPos + stringDir * fk * 0.8;
    float d = length(p - pt);
    minD = min(minD, d);
  }
  // Dashed appearance: modulate the string with a high-frequency sin.
  float dashPattern = 0.5 + 0.5 * sin(dot(p - mpPos, stringDir) * 30.0 - uTime * 4.0);
  float stringMask = (1.0 - smoothstep(0.0, 0.004, minD)) * dashPattern;
  col += vec3(0.6, 0.85, 0.95) * stringMask * 0.6;

  // "N" and "S" labels at the corners.
  // Top-right: N (north magnetic charge).
  vec2 nPos = vec2(0.55, 0.45);
  float nD = length(p - nPos);
  if (nD < 0.04) {
    col = mix(col, vec3(0.95, 0.4, 0.3), 0.7);
  }
  // Bottom-left: an "S" indicator (the pole from which field lines emerge).
  vec2 sPos = vec2(-0.55, -0.45);
  float sD = length(p - sPos);
  if (sD < 0.04) {
    col = mix(col, vec3(0.4, 0.55, 0.95), 0.7);
  }

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeMagneticMonopoleBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeMagneticMonopoleRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createMagneticMonopolePipeline(gl: WebGL2RenderingContext): MagneticMonopolePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawMagneticMonopole(
  gl: WebGL2RenderingContext,
  pipe: MagneticMonopolePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
